import normalize from "json-api-normalizer";
import _ from "lodash";
import { camelizeKeys } from "humps";
import store from "@/store/index";

import BaseService from "./BaseService";

export default {
  get(id) {
    return BaseService.get(`interviews/${id}`, "interviews")
      .then(response => {
        const data = normalize(response.data);
        // normalize doesn't traverse deep enough when camelizing keys, so we have to do it manually
        if (data.interviewPages) {
          _.forEach(Object.keys(data.interviewPages), key => {
            data.interviewPages[key].attributes = camelizeKeys(
              data.interviewPages[key].attributes
            );
          });
        }
        if (data.interviewAlternateEndings) {
          _.forEach(Object.keys(data.interviewAlternateEndings), key => {
            data.interviewAlternateEndings[key].attributes = camelizeKeys(
              data.interviewAlternateEndings[key].attributes
            );
          });
        }
        store.dispatch("interview/set", data);
        return response;
      })
      .catch(errors => Promise.reject(errors));
  },

  getDraftId(parentId) {
    return BaseService.get(`interviews/${parentId}/draft-id`, "interviews")
      .then(response => response.data.data.id)
      .catch(errors => Promise.reject(errors));
  },

  publish(id) {
    const data = {
      id
    };
    return BaseService.patch("interviews", `interviews/${id}/publish`, data)
      .then(() => {
        this.get(id);
      })
      .catch(errors => Promise.reject(errors));
  },

  list() {
    return BaseService.get("interviews/published-list")
      .then(response => {
        store.dispatch(
          "interview/setList",
          normalize(response.data).interviews
        );
      })
      .catch(errors => Promise.reject(errors));
  },

  updateInterview(id, formData) {
    const patchData = {
      id,
      ...formData
    };
    return BaseService.patch("interviews", `interviews/${id}`, patchData)
      .then(response => {
        const data = normalize(response.data);
        const values = {};
        _.forEach(Object.keys(formData), key => {
          values[key] = Object.values(data.interviews)[0].attributes[key];
        });
        store.dispatch("interview/updateInterview", values);
      })
      .catch(errors => Promise.reject(errors));
  },

  updateQuestion(question) {
    return BaseService.patch(
      "interview-questions",
      `interview_questions/${question.id}`,
      question
    )
      .then(response => {
        // normalize doesn't traverse deep enough when camelizing keys, so we have to do it manually
        const data = camelizeKeys(
          Object.values(normalize(response.data).interviewQuestions)[0]
        );
        store.dispatch("interview/updateQuestion", data);
      })
      .catch(errors => Promise.reject(errors));
  },

  addQuestion(pageId, type, dataType) {
    const data = {
      interviewPageId: pageId,
      kind: type,
      dataType
    };
    return BaseService.post("interview-questions", "interview_questions", data)
      .then(response => {
        store.dispatch(
          "interview/addQuestion",
          Object.values(normalize(response.data).interviewQuestions)[0]
        );
      })
      .catch(errors => Promise.reject(errors));
  },

  removeQuestion(questionId, pageId) {
    return BaseService.delete(`interview_questions/${questionId}`)
      .then(() => {
        store.dispatch("interview/removeQuestion", { questionId, pageId });
      })
      .catch(errors => Promise.reject(errors));
  },

  moveQuestion(id, interviewPageId, position, positionQuestionId, interviewId) {
    const data = {
      id,
      interviewPageId,
      position,
      positionQuestionId
    };
    return BaseService.patch(
      "interview-questions",
      `interview_questions/${id}/move`,
      data
    )
      .then(() => {
        this.get(interviewId);
      })
      .catch(errors => Promise.reject(errors));
  },

  addPage(interviewId) {
    const data = {
      id: interviewId
    };
    return BaseService.patch(
      "interviews",
      `interviews/${interviewId}/add-page`,
      data
    )
      .then(() => {
        this.get(interviewId);
      })
      .catch(errors => Promise.reject(errors));
  },

  deletePage(interviewId, pageId) {
    return BaseService.delete(`interviews/${interviewId}/delete-page/${pageId}`)
      .then(() => {
        this.get(interviewId);
      })
      .catch(errors => Promise.reject(errors));
  },

  updatePage(interviewId, page) {
    const data = {
      id: interviewId,
      title: page.attributes.title
    };
    return BaseService.patch(
      "interviews",
      `interviews/${interviewId}/update-page/${page.id}`,
      data
    )
      .then(() => {
        this.get(interviewId);
      })
      .catch(errors => Promise.reject(errors));
  },

  pageLogic(interviewId, page, skipTo) {
    const data = {
      id: interviewId,
      logic: { skipTo }
    };
    return BaseService.patch(
      "interviews",
      `interviews/${interviewId}/update-page/${page.id}`,
      data
    )
      .then(() => {
        this.get(interviewId);
      })
      .catch(errors => Promise.reject(errors));
  },

  addAlternateEnding(interviewId) {
    const data = {
      id: interviewId
    };
    return BaseService.patch(
      "interviews",
      `interviews/${interviewId}/add-alternate-ending`,
      data
    )
      .then(() => {
        this.get(interviewId);
      })
      .catch(errors => Promise.reject(errors));
  },

  deleteAlternateEnding(interviewId, endingId) {
    return BaseService.delete(
      `interviews/${interviewId}/delete-alternate-ending/${endingId}`
    )
      .then(() => {
        this.get(interviewId);
      })
      .catch(errors => Promise.reject(errors));
  },

  updateAlternateEnding(interviewId, ending) {
    const data = {
      id: interviewId,
      alternateEndingHeader: ending.attributes.alternateEndingHeader,
      alternateEndingMessage: ending.attributes.alternateEndingMessage,
      alternateEndingButtonLabel: ending.attributes.alternateEndingButtonLabel,
      alternateEndingButtonUrl: ending.attributes.alternateEndingButtonUrl,
      alternateEndingOnDemandMessage:
        ending.attributes.alternateEndingOnDemandMessage,
      alternateEndingOnDemandButtonText:
        ending.attributes.alternateEndingOnDemandButtonText,
      alternateEndingShowOndemandSessionButton:
        ending.attributes.alternateEndingShowOndemandSessionButton,
      statusOverride: ending.attributes.statusOverride
    };
    return BaseService.patch(
      "interviews",
      `interviews/${interviewId}/update-alternate-ending/${ending.id}`,
      data
    )
      .then(() => {
        this.get(interviewId);
      })
      .catch(errors => Promise.reject(errors));
  },

  getPool(page = 1) {
    return BaseService.get(`interviews?page[number]=${page}`)
      .then(response => {
        const payload = {
          interviews: normalize(response.data).interviews,
          links: response.data.links
        };
        store.dispatch("interview/setPool", payload);
      })
      .catch(errors => Promise.reject(errors));
  },

  create(values) {
    return BaseService.post("interviews", "interviews", values)
      .then(response => {
        const interview = normalize(response.data).interviews;
        store.dispatch("interview/add", interview);
      })
      .catch(errors => Promise.reject(errors));
  },

  update(values) {
    return BaseService.patch("interviews", `interviews/${values.id}`, values)
      .then(response => {
        const interview = normalize(response.data).interviews;
        store.dispatch("interview/update", interview);
      })
      .catch(errors => Promise.reject(errors));
  },

  delete(interviewId) {
    return BaseService.delete(`interviews/${interviewId}`)
      .then(() => {
        store.dispatch("interview/remove", interviewId);
      })
      .catch(errors => Promise.reject(errors));
  },

  getTags() {
    return BaseService.get("interviews/tags")
      .then(response => {
        store.dispatch("interview/setTags", camelizeKeys(response.data));
      })
      .catch(errors => Promise.reject(errors));
  },

  previewUrl(id) {
    const subdomain = BaseService.getSubdomain();
    const baseDomain = process.env.VUE_APP_SCHEDULER_BASE_DOMAIN;
    const protocol = process.env.VUE_APP_URL_PROTOCOL;
    let domain = `${protocol}://${subdomain}.${baseDomain}`;
    if (process.env.NODE_ENV === "development") {
      domain = `https://${subdomain}.localhost:8888`;
    }

    return `${domain}/#/preview/${id}`;
  },

  url(subdomain, keyword) {
    let url = "";

    switch (window.location.host) {
      case "healeo.dozensoft.com":
        url = `https://${subdomain}.healeo.dozensoft.com/#/${keyword}`;
        break;
      case "localhost:8080":
        url = `http://${subdomain}.localhost:8888/#/${keyword}`;
        break;
      default:
        const baseDomain = process.env.VUE_APP_SCHEDULER_BASE_DOMAIN;
        const protocol = process.env.VUE_APP_URL_PROTOCOL;
        url = `${protocol}://${subdomain}.${baseDomain}/#/${keyword}`;
        break;
    }

    return url;
  },

  sendText(interviewId, contact) {
    const data = {
      id: interviewId,
      name: contact.name,
      phone: contact.phone
    };
    return BaseService.post(
      "interviews",
      `interviews/${interviewId}/send-text`,
      data
    )
      .then(response => response.data)
      .catch(errors => Promise.reject(errors));
  },

  logs(interviewId, page) {
    return BaseService.get(
      `interviews/${interviewId}/log?page[number]=${page}&page[size]=25`
    )
      .then(response => ({
        data: normalize(response.data).interviewMessageLogs,
        links: response.data.links
      }))
      .catch(errors => Promise.reject(errors));
  },

  doctors(id) {
    return BaseService.get(`interviews/${id}/doctors`, "interviews")
      .then(response => {
        const data = normalize(response.data);
        if (data.referralDoctors) {
          store.dispatch("interview/setDoctors", data.referralDoctors);
        }
      })
      .catch(errors => Promise.reject(errors));
  }
};
